import { z } from 'zod';

import objectPassthrough from './objectPassthrough';

interface ExtensionAvailableInfoMessage {
  type: 'extensionAvailableInfo';
  heyloginVersion?: string;
  /** Set by device management, see organizations.browser_managed_device_token in the backend. */
  browserManagedDeviceToken?: string;
  globalSearch?: { shortcut?: string };
  autotype?:
    | { isInstalled: false }
    | { isInstalled: true; installVersion?: string; shortcut: string | null };
  isSimulated?: boolean;
}
const ExtensionAvailableInfoMessage = objectPassthrough({
  type: z.literal('extensionAvailableInfo'),
  heyloginVersion: z.string().optional(),
  browserManagedDeviceToken: z.string().optional(),
  globalSearch: z.object({ shortcut: z.string().optional() }).optional(),
  autotype: z.union([
    z.object({ isInstalled: z.literal(false) }),
    z
      .object({
        isInstalled: z.literal(true),
        installVersion: z.string().optional(),
        shortcut: z.string().nullable(),
      })
      .optional(),
  ]),
  isSimulated: z.boolean().optional(),
});

interface GetClientCoreParametersRequestMessage {
  type: 'getClientCoreParametersRequest';
}
const GetClientCoreParametersRequestMessage = objectPassthrough({
  type: z.literal('getClientCoreParametersRequest'),
});

interface SetClientCoreParametersMessage {
  type: 'setClientCoreParameters';
  clientCoreParametersJSON: string;
}
const SetClientCoreParametersMessage = objectPassthrough({
  type: z.literal('setClientCoreParameters'),
  clientCoreParametersJSON: z.string(),
});

type ToWebappMessage =
  | ExtensionAvailableInfoMessage
  | GetClientCoreParametersRequestMessage
  | SetClientCoreParametersMessage;
const ToWebappMessage = z.union([
  ExtensionAvailableInfoMessage,
  GetClientCoreParametersRequestMessage,
  SetClientCoreParametersMessage,
]);

interface IsExtensionAvailableMessage {
  type: 'isExtensionAvailable';
}
const IsExtensionAvailableMessage = objectPassthrough({
  type: z.literal('isExtensionAvailable'),
});

interface ClientCoreParametersChangeMessage {
  type: 'clientCoreParametersChange';
}
const ClientCoreParametersChangeMessage = objectPassthrough({
  type: z.literal('clientCoreParametersChange'),
});

interface OpenShortcutSettingsMessage {
  type: 'openShortcutSettings';
}
const OpenShortcutSettingsMessage = objectPassthrough({
  type: z.literal('openShortcutSettings'),
});

interface GetClientCoreParametersResponseMessage {
  type: 'getClientCoreParametersResponse';
  clientCoreParametersJSON?: string;
}
const GetClientCoreParametersResponseMessage = objectPassthrough({
  type: z.literal('getClientCoreParametersResponse'),
  clientCoreParametersJSON: z.string().optional(),
});

interface SetClearValueFromClipboardAfterTimeoutMessage {
  type: 'setClearValueFromClipboardAfterTimeout';
  clearAfterTimeout: boolean;
  value: string;
}
const SetClearValueFromClipboardAfterTimeoutMessage = objectPassthrough({
  type: z.literal('setClearValueFromClipboardAfterTimeout'),
  clearAfterTimeout: z.boolean(),
  value: z.string(),
});

type ToExtensionMessage =
  | IsExtensionAvailableMessage
  | ClientCoreParametersChangeMessage
  | OpenShortcutSettingsMessage
  | GetClientCoreParametersResponseMessage
  | SetClearValueFromClipboardAfterTimeoutMessage;
const ToExtensionMessage = z.union([
  IsExtensionAvailableMessage,
  ClientCoreParametersChangeMessage,
  OpenShortcutSettingsMessage,
  GetClientCoreParametersResponseMessage,
  SetClearValueFromClipboardAfterTimeoutMessage,
]);

export {
  ToWebappMessage,
  ExtensionAvailableInfoMessage,
  GetClientCoreParametersRequestMessage,
  SetClientCoreParametersMessage,
  ToExtensionMessage,
  ClientCoreParametersChangeMessage,
  IsExtensionAvailableMessage,
  OpenShortcutSettingsMessage,
  GetClientCoreParametersResponseMessage,
  SetClearValueFromClipboardAfterTimeoutMessage,
};
